import {
  AiCallIconTooltip,
  Box,
  Disclaimer,
  Div,
  H5,
  Icon,
  Img,
  InfoComponentMobile,
  ManualIconTooltip,
  P,
  PlusIcon,
  SimpleVDriveDeliveryEntity,
  StarIcon,
  StatusMark,
  TooltipWrapper,
  TruncatedText,
  displayCentsInDollars,
  formatDateTimeFull,
  formatDateTimeShort,
  isAdmin,
  lightTheme,
  renderFleetIcon,
  renderFleetImage,
} from '@vgrubs/components';
import { memo } from 'react';
import { useNavigate } from 'react-router';
import { RenderStatus } from '../DeliveriesStyled';
import { DeliveryStatusPill } from './DeliveryStatusPill';
import { DistancePill } from './DistancePill';

const DEBT_REPAYMENT_TOOLTIP =
  'Previous Transaction Due Balance: balance still due from previous vDrive transactions, repaid with this delivery under the repayment plan. Not part of the delivery total.';

const DeliveryComponentInner = ({
  delivery,
  onBeforeNavigate,
  handleClaimDelivery,
}: {
  delivery: SimpleVDriveDeliveryEntity;
  onBeforeNavigate?: () => void;
  handleClaimDelivery: (id: number) => void;
}) => {
  const navigate = useNavigate();

  const isManual = delivery?.creationType === 'manual';
  const hasAiCall =
    delivery?.interceptorCallStatus !== null &&
    delivery?.interceptorCallStatus !== undefined;

  const SideIcons = () => {
    return (
      <Div
        $flex
        $column
        $alignCenter
        $absolute
        style={{
          left: '0',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        $gap="6px"
      >
        {isManual && <ManualIconTooltip tooltip="Manual delivery" />}

        {hasAiCall && (
          <AiCallIconTooltip
            resultFailed={delivery?.interceptorCallResultFailed ?? false}
          />
        )}
      </Div>
    );
  };

  const formattedPhone = delivery?.customerPhone?.startsWith('+')
    ? delivery?.customerPhone
    : '+' + delivery?.customerPhone;
  return (
    <>
      <Box
        $height="74px"
        $p="0px 20px"
        $relative
        $desktop
        $pointer
        $flex
        $alignCenter
        $gap="12px"
        $mb="8"
        onClick={() => {
          onBeforeNavigate?.();
          navigate(`/deliveries/${delivery?.id}`);
        }}
        $borderColor={
          isManual ? lightTheme.primary : RenderStatus(delivery?.status)?.color
        }
        style={{
          boxShadow: 'none',
        }}
      >
        {!isManual && !hasAiCall && (
          <StatusMark $color={RenderStatus(delivery?.status)?.color} />
        )}

        {isAdmin && <SideIcons />}
        <Div $width={isAdmin ? '5%' : '7%'}>
          <TooltipWrapper
            tooltip={delivery?.orderId?.toString() ?? undefined}
            copy={delivery?.orderId?.toString() ?? undefined}
          >
            <Disclaimer $m="0">
              <TruncatedText underline={delivery?.orderId ? true : false}>
                {delivery?.orderId ?? '-'}
              </TruncatedText>
            </Disclaimer>
          </TooltipWrapper>
        </Div>
        <Div $width={isAdmin ? '5%' : '7%'}>
          <TooltipWrapper
            tooltip={delivery?.dspId ?? undefined}
            copy={delivery?.dspId ?? undefined}
          >
            <Disclaimer $m="0">
              <TruncatedText
                underline={
                  delivery?.dspId && delivery?.dspId.length > 0 ? true : false
                }
              >
                {delivery?.dspId && delivery?.dspId.length > 0
                  ? delivery?.dspId
                  : '-'}
              </TruncatedText>
            </Disclaimer>
          </TooltipWrapper>
          {delivery?.dspCustomerId && (
            <TooltipWrapper
              tooltip={delivery?.dspCustomerId ?? undefined}
              copy={delivery?.dspCustomerId ?? undefined}
            >
              <Disclaimer $m="0" $mt="4">
                <TruncatedText
                  underline={delivery?.dspCustomerId ? true : false}
                >
                  {delivery?.dspCustomerId}
                </TruncatedText>
              </Disclaimer>
            </TooltipWrapper>
          )}
        </Div>
        <Div $width="8%">
          <Disclaimer $m="0">
            {formatDateTimeShort(delivery?.pickupAt ?? '', delivery?.timeZone)}
          </Disclaimer>
        </Div>
        <Div $width="8%">
          <Disclaimer $m="0">
            {formatDateTimeShort(delivery?.dropoffAt ?? '', delivery?.timeZone)}
          </Disclaimer>
        </Div>
        <Div $width={isAdmin ? '5%' : '6%'}>
          <Disclaimer $m="0">
            <Img
              alt=""
              $width="50px"
              src={
                // delivery?.provider === vDriveProviders.MOTOCLICK &&
                // delivery?.vDriveSource === VDriveSource.motoclick
                //   ? dspLogo
                //   :
                renderFleetImage(delivery.provider)
              }
            />
          </Disclaimer>
        </Div>
        <Div $width="12%" $minWidth="0">
          <TooltipWrapper tooltip={delivery?.storeName}>
            <Disclaimer $m="0">
              <TruncatedText>{delivery?.storeName ?? '-'}</TruncatedText>
            </Disclaimer>
          </TooltipWrapper>
          <br />
          <TooltipWrapper tooltip={delivery?.storeNumber ?? undefined}>
            <Disclaimer $m="0">
              <TruncatedText>{delivery?.storeNumber ?? '-'}</TruncatedText>
            </Disclaimer>
          </TooltipWrapper>
          <br />
          <TooltipWrapper tooltip={delivery?.storeAddress ?? undefined}>
            <Disclaimer $m="0">
              <TruncatedText>{delivery?.storeAddress ?? '-'}</TruncatedText>
            </Disclaimer>
          </TooltipWrapper>
        </Div>
        <Div $width="10%" $flex $column $gap="4px">
          <TooltipWrapper
            tooltip={delivery?.customerName ?? undefined}
            copy={delivery?.customerName ?? undefined}
          >
            <Disclaimer $m="0">
              <TruncatedText
                underline={
                  delivery?.customerName && delivery?.customerName.length > 0
                    ? true
                    : false
                }
              >
                {delivery?.customerName && delivery?.customerName.length > 0
                  ? delivery?.customerName
                  : '-'}
              </TruncatedText>
            </Disclaimer>
          </TooltipWrapper>

          <TooltipWrapper
            tooltip={formattedPhone ?? undefined}
            copy={formattedPhone ?? undefined}
          >
            <Disclaimer $m="0">
              <TruncatedText
                underline={
                  delivery?.customerPhone && delivery?.customerPhone.length > 0
                    ? true
                    : false
                }
              >
                {delivery?.customerPhone && delivery?.customerPhone.length > 0
                  ? formattedPhone
                  : '-'}
              </TruncatedText>
            </Disclaimer>
          </TooltipWrapper>
        </Div>
        <Div $width="12%" $minWidth="0">
          <TooltipWrapper tooltip={delivery?.address}>
            <Disclaimer $m="0">
              <TruncatedText>{delivery?.address ?? '-'}</TruncatedText>
            </Disclaimer>
          </TooltipWrapper>
          {delivery?.distanceMiles !== null &&
          delivery?.distanceMiles !== undefined ? (
            <Div $mt="4">
              <DistancePill
                miles={delivery.distanceMiles}
                dspMiles={delivery.dspDistanceMiles}
              />
            </Div>
          ) : null}
        </Div>
        <Div $width="10%">
          <DeliveryStatusPill
            status={delivery?.status}
            cancellationReason={delivery?.cancellationReason}
          />
        </Div>
        {/* Admin: 12% for Amount, 8% for Dispatcher (whose Claim button is
            60px), so "Debt repaid: $4.00" fits on one line at 1280px. */}
        <Div $width={isAdmin ? '12%' : '15%'}>
          {isAdmin && (
            <Disclaimer $m="0">
              Tip: {displayCentsInDollars(delivery?.tips)}
              <br />
              DSP Fee: {displayCentsInDollars(delivery?.dspFee)}
            </Disclaimer>
          )}
          {!isAdmin ? (
            !delivery.brand.isVGrubsBrand || delivery.brand.isLocation ? (
              <Disclaimer $m="0">
                Tip: {displayCentsInDollars(delivery?.tips)}
                <br />
                DSP Fee: {displayCentsInDollars(delivery?.dspFee)}
              </Disclaimer>
            ) : (
              <Disclaimer $m="0">
                Tip: -
                <br />
                DSP Fee: -
              </Disclaimer>
            )
          ) : null}
          <Disclaimer $m="0">
            Subtotal: {displayCentsInDollars(delivery?.subTotal ?? 0)}
          </Disclaimer>
          {/* Charged with the delivery under a debt repayment plan; not
              part of the delivery's own total. */}
          {!!delivery?.debtRepayment && (
            // Block, not the wrapper's inline-block: an inline box sits on
            // its own line box and left a bigger gap above it than between
            // the lines over it.
            <TooltipWrapper
              tooltip={DEBT_REPAYMENT_TOOLTIP}
              style={{ display: 'block' }}
            >
              {/* One line: the row is a fixed 74px, which holds four lines
                  but not a fifth. The amount is at most $4.00, so with the
                  12% column this fits from 1280px up. */}
              <Disclaimer $m="0" $noWrap>
                Debt repaid: {displayCentsInDollars(delivery.debtRepayment)}
              </Disclaimer>
            </TooltipWrapper>
          )}
        </Div>
        <Div $width="5%" $flex $gap="4px" $alignCenter>
          <Icon
            $size={16}
            color={
              delivery?.driverRating?.rating
                ? lightTheme.yellow
                : lightTheme.lightGray
            }
          >
            <StarIcon />
          </Icon>
          <Disclaimer $m="0" $mt="1">
            {delivery?.driverRating?.rating ?? '-'}
          </Disclaimer>
        </Div>
        {isAdmin && (
          <Div $width="8%">
            {delivery?.dispatcher && (
              <Disclaimer $m="0">
                {delivery?.dispatcher?.firstName}{' '}
                {delivery?.dispatcher?.lastName}
              </Disclaimer>
            )}
            {!delivery?.dispatcher && (
              <Div $inlineFlex>
                <Div
                  $flex
                  $alignCenter
                  $gap="4px"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClaimDelivery(delivery?.id);
                  }}
                  $pointer
                >
                  <Icon $pointer $size={20}>
                    <PlusIcon />
                  </Icon>
                  <P $bold $m="0" $color={lightTheme.primary}>
                    Claim
                  </P>
                </Div>
              </Div>
            )}
          </Div>
        )}
      </Box>

      <Div
        $relative
        $mobile
        $pointer
        $flex
        $column
        $gap="4px"
        $box
        $p="12px 20px"
        $pSM="16px 20px"
        $mb="8"
        onClick={() => {
          onBeforeNavigate?.();
          navigate(`/deliveries/${delivery?.id}`);
        }}
        style={{
          boxShadow: 'none',
        }}
        $borderColor={
          isManual ? lightTheme.primary : RenderStatus(delivery?.status)?.color
        }
        $heightSM="160px"
      >
        {isAdmin && !isManual && !hasAiCall && (
          <StatusMark $color={RenderStatus(delivery?.status)?.color} />
        )}

        {!isAdmin && (
          <StatusMark $color={RenderStatus(delivery?.status)?.color} />
        )}

        <Div $absolute $right="20px" $top="16px">
          <Img
            alt=""
            $width="32px"
            src={
              // delivery?.provider === vDriveProviders.MOTOCLICK &&
              // delivery?.vDriveSource === VDriveSource.motoclick
              //   ? dspIcon
              //   :
              renderFleetIcon(delivery.provider)
            }
          />
        </Div>

        <Div $absolute $right="20px" $bottom="16px">
          {delivery?.distanceMiles !== null &&
          delivery?.distanceMiles !== undefined ? (
            <Div $mt="4">
              <DistancePill
                miles={delivery.distanceMiles}
                dspMiles={delivery.dspDistanceMiles}
              />
            </Div>
          ) : null}
        </Div>

        {isAdmin && <SideIcons />}

        <Div $minWidth="0" $width="calc(100% - 40px)">
          <H5 $m="0">
            <TruncatedText>{delivery?.customerName} </TruncatedText>
          </H5>
        </Div>

        <InfoComponentMobile
          title="Order ID"
          component={
            <TooltipWrapper
              tooltip={delivery?.orderId?.toString() ?? undefined}
              copy={delivery?.orderId?.toString() ?? undefined}
            >
              <Disclaimer $m="0">
                {delivery?.orderId?.toString() ?? '-'}
              </Disclaimer>
            </TooltipWrapper>
          }
        />
        <Div $minWidth="0">
          <InfoComponentMobile
            title="Dropoff"
            component={
              <TooltipWrapper
                $maxWidth="calc(100% - 50px)"
                tooltip={formatDateTimeFull(
                  delivery?.dropoffAt ?? '',
                  delivery?.timeZone
                )}
              >
                <Disclaimer $m="0">
                  <TruncatedText>
                    {formatDateTimeFull(
                      delivery?.dropoffAt ?? '',
                      delivery?.timeZone
                    )}
                  </TruncatedText>
                </Disclaimer>
              </TooltipWrapper>
            }
          />
        </Div>
        <Div $minWidth="0">
          <InfoComponentMobile
            title="Address"
            component={
              <TooltipWrapper
                $maxWidth="calc(100% - 50px)"
                tooltip={delivery?.address ?? undefined}
                copy={delivery?.address ?? undefined}
              >
                <Disclaimer $m="0">
                  <TruncatedText>{delivery?.address ?? '-'}</TruncatedText>
                </Disclaimer>
              </TooltipWrapper>
            }
          />
        </Div>

        <InfoComponentMobile
          title="Status"
          component={
            <TooltipWrapper
              tooltip={RenderStatus(delivery?.status)?.name ?? undefined}
            >
              <Disclaimer $m="0" $color={RenderStatus(delivery?.status)?.color}>
                {RenderStatus(delivery?.status)?.name ?? '-'}
              </Disclaimer>
            </TooltipWrapper>
          }
        />

        {isAdmin && (
          <>
            {delivery?.dispatcher && (
              <InfoComponentMobile
                title="Claimed By"
                component={
                  <Disclaimer $m="0">
                    {delivery?.dispatcher?.firstName}{' '}
                    {delivery?.dispatcher?.lastName}
                  </Disclaimer>
                }
              />
            )}
            {!delivery?.dispatcher && (
              <Div $inlineFlex>
                <Div
                  $flex
                  $alignCenter
                  $gap="4px"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClaimDelivery(delivery?.id);
                  }}
                  $pointer
                >
                  <Icon $pointer $size={20}>
                    <PlusIcon />
                  </Icon>
                  <P $bold $m="0" $color={lightTheme.primary}>
                    Claim
                  </P>
                </Div>
              </Div>
            )}
          </>
        )}
      </Div>
    </>
  );
};

/**
 * Memoized row component for the virtualized Deliveries list.
 * Skips re-render unless `delivery`, `onBeforeNavigate`, or
 * `handleClaimDelivery` change by reference. Callers must pass stable
 * callbacks (useCallback / setState dispatchers) for the memo to be
 * effective.
 */
export const DeliveryComponent = memo(DeliveryComponentInner);
